import React, { useEffect, useMemo, useState, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, Transition } from "@headlessui/react";
import { Paperclip, X } from "lucide-react";
import {
  getAcceptedTypes,
  buildAcceptAttr,
  isFileTypeAccepted,
} from "../utils/attachmentValidation";

const generateAttachmentId = () => Math.random().toString(36).substr(2, 9);

const createAttachmentItem = (file, overrideId) => ({
  id: overrideId || generateAttachmentId(),
  name: file?.name || "attachment",
  size: file?.size || 0,
  type: file?.type || "",
  file,
});

const normalizeAttachments = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map((item) => {
      const file = item?.file || item;
      if (!file) return null;
      return createAttachmentItem(file, item?.id);
    })
    .filter(Boolean);

const AttachmentsModal = ({
  open,
  onClose,
  onSave,
  assistantColors,
  attachments = [],
  metadataCards = [],
  fileSpecificMetadata = {},
  allowMetadata,
  attachmentConfig,
}) => {
  const { t } = useTranslation();
  const [currentAttachments, setCurrentAttachments] = useState(() =>
    normalizeAttachments(attachments)
  );
  const [currentMetadata, setCurrentMetadata] = useState(metadataCards);
  const [currentFileMetadata, setCurrentFileMetadata] = useState(() => {
    const normalized = normalizeAttachments(attachments);
    const byId = {};
    Object.entries(fileSpecificMetadata || {}).forEach(
      ([fileName, metadata]) => {
        const attachment = normalized.find((att) => att.name === fileName);
        if (attachment && metadata && metadata.length > 0) {
          byId[attachment.id] = metadata;
        }
      }
    );
    return byId;
  });
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [rejectedMessage, setRejectedMessage] = useState("");

  // Accepted types derived from the assistant's attachment_config.
  // Empty → no files accepted (safe default for unconfigured agents).
  const acceptedTypes = useMemo(() => getAcceptedTypes(attachmentConfig), [attachmentConfig]);
  const acceptAttr = useMemo(() => buildAcceptAttr(acceptedTypes), [acceptedTypes]);
  const hasAcceptedTypes = acceptedTypes.length > 0;

  const isFileAccepted = (file) => isFileTypeAccepted(file, acceptedTypes);

  // ── Bug fix: reset state every time the modal opens ──────────────────────────
  // The modal stays mounted between opens. Without this reset, files added in a
  // cancelled session (Close without Save) reappear on the next open because the
  // `attachments` prop hasn't changed, so the dependency-based effects don't re-fire.
  useEffect(() => {
    if (open) {
      setCurrentAttachments(normalizeAttachments(attachments));
      setCurrentMetadata(metadataCards);
      setSelectedFileId(null);
      setRejectedMessage("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    setCurrentAttachments(normalizeAttachments(attachments));
  }, [attachments]);

  useEffect(() => {
    setCurrentMetadata(metadataCards);
  }, [metadataCards]);

  useEffect(() => {
    const byId = {};
    Object.entries(fileSpecificMetadata || {}).forEach(
      ([fileName, metadata]) => {
        const attachment = currentAttachments.find(
          (att) => att.name === fileName
        );
        if (attachment && metadata && metadata.length > 0) {
          byId[attachment.id] = metadata;
        }
      }
    );
    setCurrentFileMetadata(byId);
  }, [fileSpecificMetadata, currentAttachments]);

  // ── Handlers ──────────────────────────────────────────────────────────────────
  const getDisplayMetadata = () => {
    if (selectedFileId === null) return currentMetadata;
    return currentFileMetadata[selectedFileId] || [];
  };

  const setDisplayMetadata = (newMetadata) => {
    if (selectedFileId === null) {
      setCurrentMetadata(newMetadata);
    } else {
      setCurrentFileMetadata((prev) => ({
        ...prev,
        [selectedFileId]: newMetadata,
      }));
    }
  };

  const handleSave = () => {
    const fileMetadataByName = {};
    Object.entries(currentFileMetadata).forEach(
      ([attachmentId, cards]) => {
        const attachment = currentAttachments.find(
          (att) => att.id === attachmentId
        );
        if (attachment && cards && cards.length > 0) {
          fileMetadataByName[attachment.name] = cards;
        }
      }
    );
    onSave({
      attachments: currentAttachments
        .map((att) => att.file)
        .filter(Boolean),
      metadata: currentMetadata,
      fileSpecificMetadata: fileMetadataByName,
    });
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  // Validate files; accepted ones are added, rejected ones trigger an error message.
  const addFiles = (files) => {
    const accepted = [];
    const rejected = [];
    files.forEach((file) => (isFileAccepted(file) ? accepted : rejected).push(file));
    if (accepted.length) {
      setCurrentAttachments((prev) => [
        ...prev,
        ...accepted.map((file) => createAttachmentItem(file)),
      ]);
    }
    if (rejected.length) {
      const names = rejected.map((f) => f.name).join(", ");
      setRejectedMessage(
        t("upload.rejected_files", { files: names }) ||
          `Tipo de archivo no permitido para este agente: ${names}`
      );
    } else {
      setRejectedMessage("");
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    addFiles([...e.dataTransfer.files]);
  };

  const handleFileInput = (e) => {
    addFiles([...e.target.files]);
    e.target.value = "";
  };

  const removeAttachment = (id) => {
    setCurrentAttachments(currentAttachments.filter((att) => att.id !== id));
    if (currentFileMetadata[id]) {
      const newFileMetadata = { ...currentFileMetadata };
      delete newFileMetadata[id];
      setCurrentFileMetadata(newFileMetadata);
    }
    if (selectedFileId === id) setSelectedFileId(null);
  };

  const formatMetadataForDisplay = (card) => {
    if (card._editingText !== undefined) return card._editingText;
    const valuesLines = card.values
      .map((v) => `    ${JSON.stringify(v)}`)
      .join(",\n");
    return `"key": ${JSON.stringify(card.key)},\n  "values": [\n${valuesLines}\n  ]`;
  };

  const addMetadataCard = () => {
    setDisplayMetadata([
      ...getDisplayMetadata(),
      {
        id: Math.random().toString(36).substr(2, 9),
        key: t("common.new_key"),
        values: [t("common.new_value")],
      },
    ]);
  };

  const removeMetadataCard = (id) => {
    setDisplayMetadata(getDisplayMetadata().filter((card) => card.id !== id));
  };

  const handleSelectFile = (attachmentId) => {
    setSelectedFileId(selectedFileId === attachmentId ? null : attachmentId);
  };

  const getSelectedFileName = () => {
    if (selectedFileId === null) return null;
    const file = currentAttachments.find((att) => att.id === selectedFileId);
    return file ? file.name : null;
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" initialFocus={false} onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-hidden">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
              leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className="relative transform bg-white rounded-2xl text-left transition-all w-full max-w-3xl flex flex-col shadow-xl border"
                style={{ maxHeight: "85vh", borderColor: assistantColors?.borderColor || "#e5e7eb" }}
              >
                {/* Header */}
                <div className="px-6 pt-6 pb-2 flex-shrink-0">
                  <Dialog.Title as="h2" className="text-lg font-medium text-gray-900">
                    {t("attachments")}
                  </Dialog.Title>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto overflow-x-hidden">
                  {/* Files section */}
                  <div className="px-6 py-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-medium text-gray-900">{t("upload.drop_files")}</h3>
                      {currentAttachments.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setCurrentAttachments([])}
                          className="text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          {t("common.delete_all")}
                        </button>
                      )}
                    </div>

                    {/* Upload area — disabled entirely when no types are accepted */}
                    {!hasAcceptedTypes ? (
                      <div className="border-2 border-dashed rounded-xl p-8 text-center border-gray-200 bg-gray-50">
                        <p className="text-sm text-gray-400">
                          {t("upload.no_types_configured") ||
                            "Este agente no tiene tipos de adjunto habilitados."}
                        </p>
                      </div>
                    ) : (
                      <div
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                          dragActive
                            ? "bg-gray-50 border-gray-400"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <input
                          type="file"
                          multiple
                          accept={acceptAttr}
                          onChange={handleFileInput}
                          className="hidden"
                          id="attachments-modal-file-input"
                        />
                        <label htmlFor="attachments-modal-file-input" className="cursor-pointer block">
                          <div className="text-xl text-gray-300 mb-1">+</div>
                          <div className="text-sm text-gray-500">{t("upload.drag_files")}</div>
                        </label>
                      </div>
                    )}

                    {rejectedMessage && (
                      <p className="mt-2 text-xs text-red-500">{rejectedMessage}</p>
                    )}

                    {currentAttachments.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2 max-h-[120px] overflow-y-auto pr-1">
                        {currentAttachments.map((attachment) => {
                          const isSelected = selectedFileId === attachment.id;
                          return (
                            <button
                              key={attachment.id}
                              type="button"
                              onClick={() => handleSelectFile(attachment.id)}
                              className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-xs transition-colors cursor-pointer border ${
                                isSelected ? "bg-gray-900 text-white" : "bg-white text-gray-700"
                              }`}
                              style={{
                                borderColor: assistantColors?.secondaryColor || "#e5e7eb",
                              }}
                            >
                              <Paperclip size={13} />
                              <span className="max-w-[140px] truncate">{attachment.name}</span>
                              <span
                                role="button"
                                tabIndex={0}
                                onClick={(e) => { e.stopPropagation(); removeAttachment(attachment.id); }}
                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); removeAttachment(attachment.id); } }}
                                className="ml-0.5 hover:opacity-70"
                              >
                                <X size={13} />
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Metadata section */}
                  {allowMetadata && (
                    <div className="px-6 py-4 border-t border-gray-100">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-medium text-gray-900">
                          {selectedFileId !== null ? t("documentMetadata") : t("generalMetadata")}
                          {selectedFileId !== null && (
                            <span className="text-gray-400 font-normal ml-2">— {getSelectedFileName()}</span>
                          )}
                        </h3>
                        {selectedFileId !== null && (
                          <button
                            type="button"
                            onClick={() => setSelectedFileId(null)}
                            className="text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
                          >
                            {t("common.show_global") || "Show Global"}
                          </button>
                        )}
                      </div>

                      <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
                        {getDisplayMetadata().map((card) => (
                          <div key={card.id} className="relative border border-gray-200 rounded-xl bg-gray-50 flex flex-col" style={{ minHeight: "120px", maxHeight: "140px", overflow: "hidden" }}>
                            <button
                              onClick={() => removeMetadataCard(card.id)}
                              className="absolute top-2 right-2 z-10 w-5 h-5 flex items-center justify-center rounded-full bg-white border border-gray-200 text-gray-400 hover:text-gray-600 hover:border-gray-300 text-xs cursor-pointer transition-colors"
                              title={t("removeMetadataBlock")}
                              type="button"
                            >
                              <X size={12} />
                            </button>
                            <textarea
                              value={formatMetadataForDisplay(card)}
                              onChange={(e) => {
                                const newCard = { ...card, _editingText: e.target.value };
                                setDisplayMetadata(getDisplayMetadata().map((c) => (c.id === card.id ? newCard : c)));
                              }}
                              onBlur={(e) => {
                                try {
                                  const parsed = JSON.parse(`{${e.target.value}}`);
                                  delete parsed._editingText;
                                  setDisplayMetadata(getDisplayMetadata().map((c) =>
                                    c.id === card.id ? { id: c.id, key: parsed.key || c.key, values: Array.isArray(parsed.values) ? parsed.values : [parsed.values || ""] } : c
                                  ));
                                } catch {
                                  setDisplayMetadata(getDisplayMetadata().map((c) =>
                                    c.id === card.id ? { ...card, _editingText: e.target.value } : c
                                  ));
                                }
                              }}
                              className="w-full flex-1 rounded-xl font-mono text-xs focus:outline-none resize-none overflow-y-auto bg-transparent p-3 pr-8"
                              style={{ wordBreak: "break-all", overflowWrap: "break-word", whiteSpace: "pre-wrap" }}
                            />
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={addMetadataCard}
                          className="border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center gap-1 text-gray-400 hover:text-gray-500 hover:border-gray-300 transition-colors cursor-pointer"
                          style={{ minHeight: "120px" }}
                        >
                          <span className="text-lg">+</span>
                          <span className="text-xs">{t("upload.add_metadata")}</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 flex justify-end gap-3 flex-shrink-0">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    {t("cancel")}
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    className="px-4 py-2 rounded-lg text-sm font-medium text-white hover:opacity-90 transition-colors cursor-pointer"
                    style={{ backgroundColor: assistantColors?.primaryColor || "#111827" }}
                  >
                    {t("save")}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default AttachmentsModal;
